import { useQuery } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { deviceAppointmentsQueryKey } from './appointment-list-keys';
import type {
  AppointmentsDetailsResponse,
  DateFilter,
  DeviceAppointmentsRequest,
} from '@/types/scheduling-api';

/**
 * POST /api/Appointment/ResourceList — the full DEVICE appointment list for a
 * date range (PROD-2382). Draws the Devices tab's Day/Week grid and feeds the
 * Technician Worklist, exactly as `useAppointments` does for practitioners.
 *
 * The device-side twin of `useAppointments`, on its own endpoint on purpose —
 * the same ruling (2026-09-13, Andrew: "create the same idea not use the same
 * api") that put `useDeviceCalendarSlots` beside `useCalendarSlots`, and for a
 * sharper reason here. Device sessions were read through the PROVIDER route
 * with the device ids stuffed into `practitioners`. That only ever worked by
 * accident: the backend's participant filter matches on reference with no type
 * check, so any id in that array matches any participant. It is not a contract —
 * the day the provider route learns to filter its own participants by type, the
 * Devices tab and the Technician Worklist empty out with nothing failing. A
 * separate route with a `devices` array says what is actually being asked for.
 *
 * Not a flag on `useAppointments` for the same reason it is not a flag on the
 * slots feed: one hook that sometimes means machines is one key, and every
 * cache-wide consumer of the provider list would then be reaching into device
 * rows it has no business rewriting.
 *
 * The RESPONSE is identical — `AppointmentsDetailsResponse`, already
 * device-safe (`provider` null on a device booking, `specialty` null since
 * PROD-2287, a `Device` participant maps like any other). So every mapper,
 * filter and grid downstream of this hook is the same code the provider list
 * feeds, unchanged.
 */

interface DeviceAppointmentsParams {
  organizationId: string;
  practiceId: string;
  startDate: string;
  endDate: string;
  filter: DateFilter;
  /**
   * Device ids. The device-side twin of `AppointmentsParams.practitioners` —
   * the one field of the request that differs.
   *
   * An EMPTY array means "no participant filter", i.e. the whole practice's
   * day, so callers with nothing to ask about must pass `null` params rather
   * than an empty list. Both current callers do.
   */
  devices?: string[];
  searchText?: string;
  status?: DeviceAppointmentsRequest['status'];
  location?: string[];
  types?: DeviceAppointmentsRequest['types'];
  pageNumber?: number;
  pageSize?: number;
  isTimeSelected?: boolean;
  showActiveAppointmentOnly?: boolean;
}

interface DeviceAppointmentsOptions {
  /**
   * Keep the PREVIOUS key's sessions on screen while a new key loads, instead
   * of dropping to `pending` (and, downstream, to a spinner). Opt-in per
   * caller, mirroring `useAppointments` — it is only right where the params
   * change UNDER a rendered list. The Technician Worklist's key is derived
   * from the device ids and the day it is showing, so adding a device in
   * Settings remints it under a rendered table.
   *
   * IT NEVER CARRIES ROWS ACROSS A DATE CHANGE — see `placeholderData` below.
   */
  keepPreviousData?: boolean;
}

/**
 * The date range a cached key was fetched for. `params` is the whole second
 * segment of the key (`deviceAppointmentsQueryKey`), so this reads the range
 * off a PREVIOUS query without that query having to hand anything over.
 */
function rangeOf(params: unknown): string {
  if (!params || typeof params !== 'object') return '';
  const { startDate, endDate } = params as {
    startDate?: unknown;
    endDate?: unknown;
  };
  return `${String(startDate ?? '')}|${String(endDate ?? '')}`;
}

export function useDeviceAppointments(
  params: DeviceAppointmentsParams | null,
  options: DeviceAppointmentsOptions = {},
) {
  return useQuery({
    // Built, not spelled out: the mutation hooks that patch and invalidate the
    // cached lists match on this root through `APPOINTMENT_LIST_QUERIES`, and a
    // key literal duplicated here could drift away from them silently.
    queryKey: deviceAppointmentsQueryKey(params),
    queryFn: ({ signal }) => {
      if (!params) throw new Error('useDeviceAppointments called without params');
      const body: DeviceAppointmentsRequest = {
        searchText: params.searchText ?? '',
        LoggedInUserTimezone:
          Intl.DateTimeFormat().resolvedOptions().timeZone,
        filter: params.filter,
        startDate: params.startDate,
        endDate: params.endDate,
        organizationId: params.organizationId,
        practiceId: params.practiceId,
        devices: params.devices ?? [],
        status: params.status ?? [],
        location: params.location ?? [],
        types: params.types ?? [],
        pageNumber: params.pageNumber ?? -1,
        pageSize: params.pageSize ?? 10,
        isTimeSelected: params.isTimeSelected ?? false,
        showActiveAppointmentOnly: params.showActiveAppointmentOnly ?? false,
      };
      return post<AppointmentsDetailsResponse>(
        '/api/Appointment/ResourceList',
        body,
        { signal },
      );
    },
    enabled: !!params?.practiceId,
    refetchOnMount: 'always',
    // `undefined` when not opted in, which is exactly "no placeholder" — React
    // Query only consults `placeholderData` when it is not `undefined`.
    //
    // WHEN OPTED IN IT IS STILL NOT PLAIN `keepPreviousData` (PROD-2408). The
    // opt-in exists for a key that remints while the SAME day is on screen —
    // a device added in Settings, a registry blip — where a blank table is
    // strictly worse than a slightly stale one. A key that remints because
    // the DAY CHANGED is the opposite case: carrying the previous rows over
    // renders Tuesday's sessions under a header that now says Wednesday,
    // which is the silent-wrong-answer failure, not a nicety. So the
    // placeholder is refused across a date change and the screen goes through
    // its own loading state — where, by the zero-versus-unknown contract, it
    // shows no count at all rather than `0`.
    //
    // This rule is the hook's, not the caller's: there is no correct surface
    // on which yesterday's rows may stand in for today's.
    placeholderData: options.keepPreviousData
      ? (previousData, previousQuery) =>
          rangeOf(previousQuery?.queryKey?.[1]) === rangeOf(params)
            ? previousData
            : undefined
      : undefined,
  });
}
